import React, { useMemo } from "react";
import { useRouteMatch } from "react-router-dom";
import { useMediaQuery } from "@mantine/hooks";

import { AppShell, Box, Group, Button, Divider } from "@mantine/core";

import { HiMiniBars3 } from "react-icons/hi2";

import ProjectSelect from "../ProjectSelect";
import RollupsSelect from "../RollupsSelect";
import NotificationsDropdown from "../NotificationsDropdown";
import UserDropdown from "../UserDropdown";

import { useAuthUserQuery } from "../../../user/hooks/queries/useAuthUserQuery";
import { useSelectedProject } from "../../../projects/hooks/useSelectedProject";
import { useUserLocalProjectsQuery } from "modules/projects/hooks/queries/useUserLocalProjectsQuery";

import { researchPrivateRoutesConstants } from "modules/research/routes/private/routesConstants";
import { ideasPrivateRoutesConstants } from "modules/ideas/routes/private/routesConstants";
import { experimentsPrivateRoutesConstants } from "modules/experiments/routes/private/routesConstants";
import { reportsPrivateRoutesConstants } from "modules/reports/routes/private/routesConstants";
import { collaborationPrivateRoutesConstants } from "modules/collaboration/routes/private/routesConstants";
import { insightsPrivateRoutesConstants } from "modules/insights/routes/private/routesConstants";
import { coreRoutesConstants } from "modules/core/routes/routesConstants";
import { projectsPrivateRoutesConstants } from "modules/projects/routes/private/routesConstants";
import { userPrivateRoutesConstants } from "modules/user/routes/private/routesConstants";
import { governancePrivateRoutesConstants } from "modules/governance/routes/private/routesConstants";

import { logout } from "modules/auth/api/auth";

const nonProjectsDropdownPaths = [
  governancePrivateRoutesConstants.people_stats.path,
  governancePrivateRoutesConstants.user_details.path,
  coreRoutesConstants.project_grouping.path
];

const rollupsPaths = [
  experimentsPrivateRoutesConstants.root.path,
  insightsPrivateRoutesConstants.project_stats.path,
  insightsPrivateRoutesConstants.query_engine.path,
  insightsPrivateRoutesConstants.roadmap.path,
  ideasPrivateRoutesConstants.root.path,
  coreRoutesConstants.dashboard.path,
  coreRoutesConstants.account_settings.path,
  researchPrivateRoutesConstants.root.path,
  reportsPrivateRoutesConstants.root.path,
  collaborationPrivateRoutesConstants.todos.path,
  projectsPrivateRoutesConstants.settings.path,
  insightsPrivateRoutesConstants.insights_repository.path
];

const Header = () => {
  const hideProjectsDropdown = useRouteMatch({
    path: nonProjectsDropdownPaths,
    exact: true
  });

  const isRollupsRoute = useRouteMatch({
    path: rollupsPaths,
    exact: true,
    strict: true
  });

  const showRollupsSelect = isRollupsRoute;

  const { data: userData } = useAuthUserQuery();

  const { selectedProject, setSelectedProject, isSingleProject } =
    useSelectedProject();
  const { data: projects } = useUserLocalProjectsQuery();

  const projectsOptions = useMemo(
    () =>
      projects
        ? projects.map(project => ({
            id: project.id,
            name: project.name
          }))
        : [],
    [projects]
  );

  const isLargeScreenUp = useMediaQuery("(min-width: 992px)");

  const userDropdownRole = useMemo(() => {
    if (!isSingleProject) {
      return userData?.best_role;
    }

    const projectRole = (selectedProject?.user_role || [])[0];

    return projectRole;
  }, [selectedProject, userData, isSingleProject]);

  return (
    <AppShell.Header>
      <Group px="md" h="100%" wrap="nowrap" justify="space-between">
        <Group gap="sm">
          {!hideProjectsDropdown && (
            <>
              <Box w={223}>
                {showRollupsSelect ? (
                  <RollupsSelect
                    value={selectedProject?.id || ""}
                    onChange={setSelectedProject}
                  />
                ) : (
                  <ProjectSelect
                    value={selectedProject?.id || ""}
                    onChange={setSelectedProject}
                    projects={projectsOptions}
                  />
                )}
              </Box>

              {selectedProject?.id !== "all" && isLargeScreenUp && (
                <>
                  <Divider orientation="vertical" h={32} />

                  <Button
                    onClick={() => setSelectedProject("all")}
                    leftSection={<HiMiniBars3 />}
                    variant="outline"
                    color="violet"
                    size="xs"
                  >
                    View all projects
                  </Button>
                </>
              )}
            </>
          )}
        </Group>

        <Group gap="xs">
          <Button
            component="a"
            href="https://efestra.crunch.help/en"
            target="_blank"
            rel="noopener noreferrer"
            variant="subtle"
            color="dark"
            size="xs"
          >
            Help Center
          </Button>

          <NotificationsDropdown />

          {userData && userDropdownRole ? (
            <Box ml={{ base: 10, md: 45 }}>
              <UserDropdown
                userPhoto={userData?.avatar}
                userRole={userDropdownRole}
                userFullname={`${userData?.name} ${userData?.surname}`}
                profileUrl={userPrivateRoutesConstants.user_profile.href()}
                accountSettingsUrl={coreRoutesConstants.account_settings.href(
                  selectedProject?.id || ""
                )}
                onLogoutClick={logout}
                accounts={userData?.accounts || []}
              />
            </Box>
          ) : null}
        </Group>
      </Group>
    </AppShell.Header>
  );
};

export default React.memo(Header);
